// Tenant-keyed react-query over a fetchAllPaged page factory — the query
// skeleton every register page repeats (key + tenantId, enabled gate, paged
// fetch, throw-on-error). Extracted when the BigTrade pages cloned it a
// fourth time (fallow dup 3cca77c2). RLS does the scoping; the tenant id is
// only in the key so a tenant switch refetches.
import { useQuery } from '@tanstack/react-query'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { useActiveTenant } from '@/lib/useActiveTenant'

type Page<T> = PromiseLike<{ data: T[] | null; error: { message: string } | null }>

export function useTenantPagedQuery<T>(
  // A string for the plain register case. An ARRAY when the page has a
  // server-side window that must be part of the key (the Receive-pallets
  // register's 90-day / All-history cutoff): keep the register name as the
  // first element so a `['pallet-receipts']` prefix invalidation still hits.
  key: string | readonly unknown[],
  makePage: (from: number, to: number) => Page<T>,
  // Optional cache profile for offline-capture lookups (networkMode/staleTime/
  // gcTime, as useWorkerNames) — defaults unchanged for the register pages.
  opts?: { networkMode?: 'online' | 'always' | 'offlineFirst'; staleTime?: number; gcTime?: number },
) {
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null
  return useQuery({
    queryKey: [...(typeof key === 'string' ? [key] : key), tenantId],
    enabled: !!tenantId,
    ...(opts ?? {}),
    queryFn: async () => {
      // paged-order-ignore makePage is the CALLER's page factory — the order is
      // theirs to impose and dal:paged-order judges it at their call site.
      const { rows, error } = await fetchAllPaged<T>(makePage)
      if (error) throw new Error(error)
      return rows
    },
  })
}
